import { Box, Button, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import PublicIcon from '@mui/icons-material/Public';
import SpaIcon from '@mui/icons-material/Spa';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { PRIMARY, EASE_OUT_QUINT, EASE_MATERIAL } from './howItWorksConstants';

export const BLOCK_WIDTH = 280;

export const StartDemoButton = ({ onClick }: { onClick: () => void }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 6, md: 8 } }}>
    <Button
      variant="contained"
      size="large"
      onClick={onClick}
      startIcon={<PlayArrowIcon />}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 15,
        py: 1.4,
        px: 3.5,
        borderRadius: 50,
        bgcolor: PRIMARY,
        boxShadow: `0 6px 20px ${PRIMARY}33`,
        '&:hover': {
          bgcolor: PRIMARY,
          boxShadow: `0 8px 24px ${PRIMARY}55`,
        },
      }}
    >
      Voir la démo
    </Button>
  </Box>
);

export interface CursorProps {
  visible: boolean;
  clicking: boolean;
  /** Position relative au parent en px depuis bottom-right du bloc */
  bottom: number;
  right: number;
}

export const Cursor = ({ visible, clicking, bottom, right }: CursorProps) => (
  <AnimatePresence>
    {visible && (
      <motion.div
        initial={{ opacity: 0, x: 30, y: 30 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.45, ease: EASE_OUT_QUINT as [number, number, number, number] }}
        style={{
          position: 'absolute',
          bottom,
          right,
          pointerEvents: 'none',
          zIndex: 5,
          width: 22,
          height: 22,
        }}
      >
        <motion.div
          animate={clicking ? { scale: [1, 0.82, 1] } : { scale: 1 }}
          transition={{ duration: 0.25, ease: EASE_MATERIAL as [number, number, number, number] }}
          style={{ width: '100%', height: '100%', position: 'relative' }}
        >
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))' }}
          >
            <path
              d="M5,2 L5,18 L9,14.5 L11.2,19.6 L13.4,18.7 L11.2,13.6 L17,13.6 Z"
              fill="#1a1a1a"
              stroke="#fff"
              strokeWidth="1.4"
              strokeLinejoin="round"
            />
          </svg>
          <AnimatePresence>
            {clicking && (
              <motion.div
                initial={{ scale: 0, opacity: 0.5 }}
                animate={{ scale: 2.6, opacity: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                style={{
                  position: 'absolute',
                  left: 4,
                  top: 4,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  backgroundColor: PRIMARY,
                  pointerEvents: 'none',
                }}
              />
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Arrow = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: PRIMARY,
      px: { xs: 0, md: 0.5 },
      py: { xs: 0.5, md: 0 },
      transform: { xs: 'rotate(90deg)', md: 'none' },
    }}
  >
    <svg width="44" height="20" viewBox="0 0 44 20" fill="none">
      <line
        x1="2"
        y1="10"
        x2="36"
        y2="10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <polyline
        points="30,4 38,10 30,16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

export interface CursorState {
  visible: boolean;
  clicking: boolean;
}

export const FacebookAd = ({
  cursor,
  heroTitle = 'Trouvez votre praticien',
  ctaLabel = 'Être rappelé',
}: {
  cursor: CursorState;
  heroTitle?: string;
  ctaLabel?: string;
}) => (
  <Box
    sx={{
      bgcolor: '#fff',
      borderRadius: 2,
      border: '1px solid #e4e6eb',
      boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
      overflow: 'hidden',
      width: BLOCK_WIDTH,
      position: 'relative',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, pt: 1.2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: PRIMARY,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        A
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>
          AlloClients
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <Typography sx={{ fontSize: 10, color: '#65676b' }}>Sponsorisé · </Typography>
          <PublicIcon sx={{ fontSize: 10, color: '#65676b' }} />
        </Box>
      </Box>
    </Box>
    <Box
      sx={{
        aspectRatio: '16 / 9',
        background: 'linear-gradient(135deg, #dbeafe 0%, #ede9fe 60%, #fce7f3 100%)',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 30% 70%, rgba(11,90,240,0.18) 0%, transparent 60%), radial-gradient(ellipse at 70% 30%, rgba(139,92,246,0.18) 0%, transparent 60%)',
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          zIndex: 1,
        }}
      >
        <SpaIcon sx={{ fontSize: 32, color: PRIMARY }} />
        <Typography
          sx={{ fontSize: 14, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.01em' }}
        >
          {heroTitle}
        </Typography>
        <Typography sx={{ fontSize: 10, color: '#475569', fontWeight: 500 }}>
          Réflexologie, massage, reiki...
        </Typography>
      </Box>
    </Box>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 1.5,
        py: 1.2,
        bgcolor: '#f0f2f5',
        borderTop: '1px solid #e4e6eb',
      }}
    >
      <Typography
        sx={{
          fontSize: 9,
          color: '#65676b',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          flex: 1,
          minWidth: 0,
        }}
      >
        allo-clients.com
      </Typography>
      <Box
        sx={{
          bgcolor: '#e4e6eb',
          borderRadius: 1,
          px: 1.2,
          py: 0.7,
          fontSize: 12,
          fontWeight: 700,
          color: '#1a1a1a',
          whiteSpace: 'nowrap',
        }}
      >
        {ctaLabel}
      </Box>
    </Box>
    <Cursor visible={cursor.visible} clicking={cursor.clicking} bottom={14} right={10} />
  </Box>
);

export const Caption = ({ number, children }: { number: number; children: React.ReactNode }) => (
  <Typography
    sx={{
      fontSize: 15,
      color: PRIMARY,
      textAlign: 'center',
      maxWidth: BLOCK_WIDTH,
      mt: 1.8,
      lineHeight: 1.4,
      fontWeight: 700,
    }}
  >
    {number}. {children}
  </Typography>
);
